/**
 * Знакомство с главной — при первом входе (03.11.2026).
 *
 * Владелец: «после входа впервые нужны всплывающие подсказки, что где
 * находится». Тот же вид, что у подсказки свайпа (`swipe-coach.tsx`): экран
 * затемнён, текст белым, кнопка салатовая. Отличие одно — подсвечено место,
 * о котором речь: «Можно тратить», «Записать», план, разделы, профиль.
 *
 * Шаги, которых на экране нет (у нового человека нет «Проверь сегодня»),
 * пропускаются. Пока знакомство открыто, нажать то, что под ним, нельзя:
 * человек, целивший в «Дальше», не должен попасть в «Записать».
 *
 * Меняется главная — меняются и шаги здесь (CLAUDE.md, «Подсказки-обучение»).
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { dismissHint } from './store.js';

/** Ключ «уже видел» в `dismissedHints`. */
export const HOME_TOUR_HINT = 'home-tour';

interface Step { target: string; title: string; text: string }

const STEPS: Step[] = [
  { target: '.gh', title: 'Сколько можно тратить',
    text: 'Деньги до ближайшего прихода — платежи и отложенное уже вычтены. Стрелка справа покажет, как посчитано.' },
  { target: '.acts', title: 'Записать трату или приход',
    text: 'Вручную, голосом или фото чека. «Могу купить?» — проверить покупку заранее.' },
  { target: '.grail', title: 'Проверь сегодня',
    text: 'Платежи, по которым нужен ответ: оплачено или нет. Пока ответа нет, деньги на них отложены.' },
  { target: '.planrows', title: 'План на месяц',
    text: 'Сколько ушло на повседневные нужды и свободные траты и сколько осталось. Значок календаря — с какого дня считать.' },
  { target: 'nav.tabs', title: 'Разделы',
    text: 'Деньги — счета, кредиты и платежи. Операции — все записи. Аналитика — куда уходят деньги. Цели — на что копишь.' },
  { target: '.hcaps', title: 'Профиль и настройки',
    text: 'Профиль, уведомления и шестерёнка с настройками. Там же «Помощь» и частые вопросы.' },
];

/** Сквозные проверки (`tools/`) жмут кнопки сразу после входа; знакомство проверяют с флагом `skoka:coach`. */
function automated(): boolean {
  if (typeof navigator === 'undefined' || !navigator.webdriver) return false;
  try { return localStorage.getItem('skoka:coach') !== '1'; } catch { return true; }
}

let shown = false;
/** Показать ли знакомство: не видели ни раньше, ни за этот запуск. */
export const tourWanted = (dismissed: readonly string[]): boolean =>
  !dismissed.includes(HOME_TOUR_HINT) && !shown && !automated();

type Box = { top: number; left: number; width: number; height: number };

export function HomeTour({ onDone }: { onDone: () => void }) {
  // Шаги — после того как главная легла в документ: при первой отрисовке её ещё нет.
  const [steps, setSteps] = useState<Step[] | null>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setSteps(STEPS.filter((s) => document.querySelector(s.target))), 600);
    return () => window.clearTimeout(t);
  }, []);
  const [at, setAt] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const step = steps?.[at];

  const finish = () => { shown = true; void dismissHint(HOME_TOUR_HINT); onDone(); };

  useLayoutEffect(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox({ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step]);

  useEffect(() => { if (steps && steps.length === 0) finish(); }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!steps || !step || !box) return null;

  const last = at === steps.length - 1;
  // Текст — там, где больше места: под подсвеченным или над ним.
  const below = box.top + box.height + 16;
  const roomBelow = window.innerHeight - below;
  const place = roomBelow > 200 || roomBelow > box.top
    ? { top: below } : { bottom: window.innerHeight - box.top + 16 };

  return createPortal(
    <div className="tour" role="dialog" aria-label={step.title}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <div className="tour-hole" style={box} aria-hidden="true" />
      <div className="tour-box" style={place}>
        <span className="tour-n">{at + 1} из {steps.length}</span>
        <b className="coach-title">{step.title}</b>
        <p className="coach-text">{step.text}</p>
        <div className="tour-acts">
          {!last && <button type="button" className="tour-skip" onClick={finish}>Пропустить</button>}
          <button type="button" className="coach-ok" onClick={() => (last ? finish() : setAt(at + 1))}>
            {last ? 'Понятно' : 'Дальше'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
